/**
 * Single source of truth for the portfolio math the chart's hero chip
 * and the Metrics cards both read. Centralising these here keeps the
 * two surfaces from drifting — historically each re-implemented the
 * same formula by hand, picked up subtle unit differences
 * (percent-scale vs. decimal) and ended up displaying numbers that
 * didn't match the graph (Sergey Apr-2026: "YTD 2026 −3.88 % in the
 * card but −3.31 % on the chart", "All-time +1316.40 %", etc.).
 *
 * UNIT CONVENTIONS — read this before touching
 * --------------------------------------------
 * Inputs (what's stored on `BrokerAccount`):
 *   • `twrCurve`, `spyCurve`, `spyTotalCurve` → PERCENT-SCALE.
 *     A value of 13.16 means +13.16 % cumulative return since
 *     inception. Zero is the anchor (first day always = 0).
 *   • `equityCurve` → dollars.
 *   • `cashflows[].amount` → signed dollars. Positive = deposit,
 *     negative = withdrawal.
 *
 * Outputs (what these helpers emit):
 *   • `*ReturnDecimal` → DECIMAL. 0.1316 means +13.16 %. Call
 *     `formatPct` (which multiplies by 100) to render.
 *   • `windowDollarReturn` → signed dollars. "Returns only" —
 *     start-to-end delta with every post-start cashflow subtracted
 *     out, same formula the chart's hero emits as `usdChange`.
 *
 * Don't duplicate. If you need a new aggregation (e.g. a MTD window)
 * add it here; never inline another variant of these formulas in a
 * component file.
 */

export interface CashflowLike {
  readonly date: string;
  readonly amount: number;
}

/**
 * Compound-ratio window return on a cumulative percent-scale curve.
 *
 *   (1 + cur %/100) / (1 + open %/100) − 1       → DECIMAL
 *
 * Matches the chart's hero-chip math for the "total-equity" basis
 * (real accounts) in `PortfolioVsSpyChart.onHoverState`. Callers can
 * use this for TWR, SPY, or any other cumulative return series that
 * anchors at 0 % on day zero.
 *
 * Why not plain subtraction? Subtraction is an additive approximation
 * that only works when the pre-window level is small. For a curve
 * that's already up +25 % going into a range, it overstates the window
 * return by a factor of (1 + 0.25). The ratio form is the correct
 * window-scoped compound return.
 */
export function windowReturnDecimal(
  curve: readonly number[],
  startIdx: number,
  endIdx: number
): number {
  const a0 = curve[startIdx] ?? 0;
  const a1 = curve[endIdx] ?? 0;
  const factorOpen = 1 + a0 / 100;
  const factorCur = 1 + a1 / 100;
  if (factorOpen === 0) return 0;
  return factorCur / factorOpen - 1;
}

/**
 * Re-anchor a whole CUMULATIVE-return series (percent scale) so it
 * reads 0 % at its first point — the series-wide form of
 * {@link windowReturnDecimal}, and the single source of truth for
 * rebasing anywhere in the app.
 *
 *     rebased[i] = ((1 + v[i]/100) / (1 + v[0]/100) − 1) × 100
 *
 * Sergey 2026-08-03: the chart lines and the range Max-Drawdown /
 * Sharpe slices each used to rebase by subtraction, which is why the
 * chart's SPY chip (+24.66 %) disagreed with the Range Return card
 * (+20.04 %) on a window that opened at +23.05 % cumulative. Everything
 * routes through here now so the chips, the plotted lines and the
 * metric cards can't drift apart again.
 */
export function rebaseCumulativePct(series: readonly number[]): number[] {
  if (series.length === 0) return [];
  const base = series[0] ?? 0;
  const baseFactor = 1 + base / 100;
  // Degenerate base (−100 %) would divide by zero — fall back to the
  // additive form there rather than emitting NaN.
  if (!Number.isFinite(baseFactor) || Math.abs(baseFactor) < 1e-9) {
    return series.map((v) => +(v - base).toFixed(4));
  }
  return series.map((v) => +(((1 + v / 100) / baseFactor - 1) * 100).toFixed(4));
}

/**
 * Window dollar return — "returns only" delta between two dates,
 * cashflow-neutralised.
 *
 *   curEquity − startEquity − sum(cashflows strictly after startDate)
 *
 * Mirrors the chart's hero `usdChange` formula. A deposit that lands
 * inside the window boosts `curEquity` but isn't market return, so we
 * subtract it out; a withdrawal lowers `curEquity` but isn't a loss,
 * so subtracting its signed (negative) amount adds it back in.
 *
 * Boundary convention: cashflows dated EXACTLY ON the start date are
 * considered part of the opening balance (already in `startEquity`)
 * and are NOT subtracted. This matches the chart's
 * `cf.xIdx > 0 && cf.xIdx <= activeIdx` filter (xIdx 0 = opening day
 * deposit, already baked into baseUsdAbs).
 */
export function windowDollarReturn(
  equityCurve: readonly number[],
  curveDates: readonly string[],
  cashflows: readonly CashflowLike[],
  startIdx: number,
  endIdx: number
): number {
  if (
    startIdx < 0 ||
    endIdx < 0 ||
    startIdx >= curveDates.length ||
    endIdx >= curveDates.length ||
    startIdx >= equityCurve.length ||
    endIdx >= equityCurve.length
  ) {
    return 0;
  }
  const startDate = curveDates[startIdx]!;
  const endDate = curveDates[endIdx]!;
  const startEquity = equityCurve[startIdx] ?? 0;
  const curEquity = equityCurve[endIdx] ?? 0;
  let cashflowContribution = 0;
  for (const cf of cashflows) {
    if (cf.date > startDate && cf.date <= endDate) {
      cashflowContribution += cf.amount;
    }
  }
  return curEquity - startEquity - cashflowContribution;
}

/**
 * First curveDates index whose date is ≥ `fromIso`. Returns the last
 * index if nothing matches (so a window that straddles beyond the
 * data still has a usable boundary). Used by every calendar-bounded
 * card (Weekly = today − 7d, YTD = Jan 1, etc.) so they pick the
 * same boundary the chart's `sliceByRange` would pick.
 */
export function findCurveIdxOnOrAfter(
  curveDates: readonly string[],
  fromIso: string
): number {
  const n = curveDates.length;
  if (n === 0) return 0;
  for (let i = 0; i < n; i++) {
    const d = curveDates[i];
    if (d && d >= fromIso) return i;
  }
  return Math.max(0, n - 1);
}

/**
 * Build the Jan-1 ISO string for the year of `asOfDate`. Used by YTD
 * window logic. Works in UTC so it doesn't cross a day boundary on
 * callers in timezones west of UTC near midnight — we want the
 * account's as-of year, not the local-wall-clock year, since the
 * account's curveDates are UTC ISO strings.
 */
export function jan1IsoForAsOfDate(asOfIso: string): string {
  const asOf = new Date(asOfIso);
  const year = Number.isFinite(asOf.getUTCFullYear())
    ? asOf.getUTCFullYear()
    : new Date().getUTCFullYear();
  return `${year}-01-01`;
}

/**
 * ISO date N calendar days before `asOfIso`. Used to find the
 * window-start for W / 1M / 3M / 6M / 1Y cards without re-deriving
 * the calendar math in every caller.
 */
export function daysBackIso(asOfIso: string, days: number): string {
  const base = new Date(asOfIso);
  const d = new Date(base);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
}

// ---- Simple moving average (SMA) ----------------------------------------

/**
 * Compute a TRAILING simple moving average over `curve` with `window`
 * samples. The first `window − 1` outputs are `null` because the
 * trailing window can't be filled — the chart should leave those days
 * unrendered (MUI X line series interpret null as a gap).
 *
 * Trailing convention matches every brokerage / TradingView preset
 * SMA. We don't offer centered SMA — it would require future-knowledge
 * (lookahead) on the right edge of the line, which doesn't exist on
 * a "today" chart.
 */
export function computeSMA(
  curve: readonly number[],
  window: number
): Array<number | null> {
  const n = curve.length;
  if (window <= 1 || n === 0) return curve.slice();
  const out: Array<number | null> = new Array(n).fill(null);
  if (n < window) return out;
  let sum = 0;
  for (let i = 0; i < window; i++) sum += curve[i] ?? 0;
  out[window - 1] = +(sum / window).toFixed(4);
  for (let i = window; i < n; i++) {
    sum += (curve[i] ?? 0) - (curve[i - window] ?? 0);
    out[i] = +(sum / window).toFixed(4);
  }
  return out;
}

/** Range key emitted by the chart's `onRangeChange` callback. Mirrors
 *  the chart's internal `RangeKey` union; redefined here so chart-math
 *  doesn't have to import from the component layer. */
export type SmaRangeKey =
  | "W"
  | "1M"
  | "3M"
  | "6M"
  | "YTD"
  | "1Y"
  | "ALL"
  | "CUSTOM";

/**
 * Pick the auto-default SMA window length for a given chart range.
 *
 * Mapping (Sergey 2026-04-30):
 *   • W       → 0  (no SMA — only ~5 trading days)
 *   • 1M      → 5
 *   • 3M      → 10
 *   • 6M      → 20
 *   • YTD     → 30  (~10 % of a full year)
 *   • 1Y      → 50  (classic 50-day TA preset)
 *   • ALL     → 50  for accounts < ~2 years; 100 for older
 *   • CUSTOM  → ~10 % of the visible window, snapped to {5,10,20,30,50,100,200}
 *
 * Returns 0 when no SMA should render (W, or CUSTOM ranges shorter
 * than the smallest preset). Callers should treat 0 as "skip the SMA
 * line entirely for this range".
 *
 * `customWindowDays` is only consulted when `range === "CUSTOM"`. It's
 * the count of calendar days the user picked in the date dialog
 * (computed by the chart from its sliced.dates).
 *
 * `accountLifespanDays` lets the ALL bucket choose 50 vs 100 — pass
 * the account's full-curve length in calendar days. Defaults to 0
 * which always picks 50.
 */
export function defaultSmaWindowForRange(
  range: SmaRangeKey,
  customWindowDays = 0,
  accountLifespanDays = 0
): number {
  switch (range) {
    case "W":
      return 0;
    case "1M":
      return 5;
    case "3M":
      return 10;
    case "6M":
      return 20;
    case "YTD":
      return 30;
    case "1Y":
      return 50;
    case "ALL":
      return accountLifespanDays >= 730 ? 100 : 50;
    case "CUSTOM": {
      const target = Math.round(customWindowDays / 10);
      if (target < 5) return 0;
      // Snap to the nearest preset. `best` typed as `number` — the
      // `as const` tuple makes each entry a literal type, which would
      // narrow `best` to `5` and reject reassignment to `10`, `20`, …
      // (Sergey 2026-04-30: tsc TS2322 caught this on the build).
      const presets: readonly number[] = [5, 10, 20, 30, 50, 100, 200];
      let best: number = presets[0]!;
      let bestDist = Math.abs(target - best);
      for (const p of presets) {
        const d = Math.abs(target - p);
        if (d < bestDist) {
          best = p;
          bestDist = d;
        }
      }
      return best;
    }
  }
}

/**
 * Resolve the SMA window the chart should actually use right now,
 * given the user's mode preference and the active range.
 *
 *   • mode === "auto"  → `defaultSmaWindowForRange(range, …)`
 *   • mode === "fixed" → `Math.max(2, fixedDays)` so we never feed
 *                         `computeSMA` a degenerate window.
 *
 * Returns 0 when the SMA line should not render at all.
 */
export function resolveSmaWindow(
  mode: "auto" | "fixed",
  fixedDays: number,
  range: SmaRangeKey,
  customWindowDays = 0,
  accountLifespanDays = 0
): number {
  if (mode === "fixed") {
    if (!Number.isFinite(fixedDays) || fixedDays < 2) return 0;
    return Math.min(500, Math.round(fixedDays));
  }
  return defaultSmaWindowForRange(
    range,
    customWindowDays,
    accountLifespanDays
  );
}
